import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Loader2, ArrowRight } from 'lucide-react';
import apiClient from '../../services/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { useT } from '../../context/LanguageContext';

/**
 * The plans a branch runs — the way in to the planner.
 *
 * A PLAN is one buffet configured once over a long range and bent by rules,
 * replacing the template + per-date-opening pair where configuring a single
 * Wednesday meant creating a whole record carrying a copy of everything.
 *
 * Plans arrive one of two ways: converted from the templates that already
 * exist (`manage.py convert_buffets_to_plans`, run by an administrator), or
 * built in the setup wizard. The empty state explains that in the reader's
 * own terms and sends them back to Buffet Templates — an empty list with no
 * explanation reads as a broken screen, and a Django command printed on it
 * reads as a screen built for somebody else.
 */

const money = (n) => (n == null ? '—' : `Rs ${Math.round(n).toLocaleString('en-IN')}`);

const STATUS_TONE = {
  PUBLISHED: 'bg-primary/10 text-primary',
  DRAFT: 'bg-muted text-muted-foreground',
  ARCHIVED: 'bg-muted text-muted-foreground line-through',
};

export default function BuffetPlans() {
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const { t } = useT();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/buffet-plans/`);
      setPlans(data?.success ? data.data : []);
      if (!data?.success) setError(data?.error || 'Could not load plans.');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const open = (plan) => navigate(
    restaurantId
      ? `/restaurant/${restaurantId}/buffet-plans/${plan.id}/calendar`
      : `/buffet-plans/${plan.id}/calendar`);

  return (
    <div className="p-6 w-full">
      <header className="mb-5">
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
          {t('plan.list.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('plan.list.sub')}</p>
      </header>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('plan.list.loading')}
        </div>
      ) : plans.length === 0 ? (
        /* An empty list here almost always means the conversion has not been
           run yet, not that the branch has no buffets — so it says so rather
           than looking like a screen that failed to load. */
        <div className="rounded-xl border border-border bg-card p-6">
          <CalendarRange className="mb-3 h-6 w-6 text-muted-foreground" />
          <h2 className="text-sm font-medium text-foreground">{t('plan.list.emptyTitle')}</h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            {t('plan.list.emptyBody')}
          </p>
          {/* NOT the conversion command. This screen is read by the people who
              run the restaurant, and `manage.py convert_buffets_to_plans` is
              neither something they can type nor something they should: the
              conversion reports first and names the price rules it cannot
              carry across, so a person has to read it before anything is
              written. What they CAN do is carry on where their buffets still
              live, so that is what the button offers. */}
          <button
            type="button"
            onClick={() => navigate(restaurantId ? `/restaurant/${restaurantId}/buffets` : '/buffets')}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground"
          >
            {t('plan.list.emptyCta')} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {plans.map((plan) => (
            <li key={plan.id}>
              <button type="button" onClick={() => open(plan)}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent/40">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{plan.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                      STATUS_TONE[plan.status] || 'bg-muted text-muted-foreground'}`}>
                      {plan.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                    {plan.date_from} – {plan.date_to} · {plan.window_start}–{plan.window_end}
                    {' · '}{t('plan.list.rules', { n: (plan.rules || []).length })}
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {money(plan.adult_price)}
                  </span>
                  <ArrowRight size={15} className="text-muted-foreground" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
